import { useState } from "react";
import { toast } from "sonner";
import { ChevronRight, FileText, Mail, PackageCheck, PenLine, BellRing } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { SignatureDialog } from "@/components/SignatureDialog";
import { RestitutionDialog } from "@/components/RestitutionDialog";
import { TONES } from "@/components/dashboard/parts";
import { api, formatApiError } from "@/lib/api";
import { WORKSHOP_STAGES, STAGE_TONE, fmtParis, fmtDate } from "@/lib/constants";

const KEYS = Object.keys(WORKSHOP_STAGES);

export const openDepositPdf = async (id) => {
  const { data } = await api.get(`/interventions/${id}/deposit-pdf`, { responseType: "blob" });
  window.open(URL.createObjectURL(data), "_blank");
};

export const daysSince = (iso) => (iso ? Math.floor((Date.now() - new Date(iso).getTime()) / 86400000) : 0);

const StageBar = ({ stage, onPick, busy }) => (
  <ol className="grid grid-cols-2 gap-1.5 sm:grid-cols-3 lg:grid-cols-6" data-testid="workshop-stage-bar">
    {KEYS.map((k, i) => {
      const done = KEYS.indexOf(stage) >= i;
      const t = TONES[STAGE_TONE[k]];
      return (
        <li key={k}>
          <button type="button" disabled={busy} onClick={() => onPick(k)} data-testid={`workshop-stage-${k}`}
            className={`flex min-h-[48px] w-full items-center gap-2 rounded-lg border px-2 py-1.5 text-left text-xs ${k === stage ? t.soft + " text-white" : done ? "border-white/10 text-slate-300" : "border-white/5 text-slate-500"}`}>
            <span className={`h-2 w-2 shrink-0 rounded-full ${done ? t.dot : "bg-slate-700"}`} />{WORKSHOP_STAGES[k]}
          </button>
        </li>
      );
    })}
  </ol>
);

const NotifyDialog = ({ inter, reminder, onClose, onSent }) => {
  const [addr, setAddr] = useState(false);
  const [preview, setPreview] = useState(null);
  const body = { reminder, include_address: addr };
  const show = () => api.post(`/interventions/${inter.id}/workshop/notify`, { ...body, preview: true }).then((r) => setPreview(r.data)).catch((e) => toast.error(formatApiError(e)));
  const send = async () => {
    try {
      await api.post(`/interventions/${inter.id}/workshop/notify`, body);
      toast.success("E-mail envoyé au client");
      onSent();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto border-cyan-400/20 bg-[#0F1F3D] sm:max-w-2xl" data-testid="workshop-notify-dialog">
        <DialogHeader><DialogTitle>{reminder ? "Rappel : matériel à récupérer" : "Prévenir le client : matériel prêt"}</DialogTitle></DialogHeader>
        <label className="flex items-center gap-3 text-sm text-slate-200">
          <Checkbox checked={addr} onCheckedChange={(v) => { setAddr(!!v); setPreview(null); }} data-testid="workshop-notify-address" />Inclure l'adresse de récupération (Paramètres)
        </label>
        {preview ? (
          <div className="space-y-2" data-testid="workshop-notify-preview">
            <p className="text-xs text-slate-400">À : {preview.to} · {preview.subject}</p>
            <iframe title="Aperçu" srcDoc={preview.html} className="h-80 w-full min-w-0 rounded-lg bg-white" style={{ zoom: 1 }} />
          </div>
        ) : <Button variant="secondary" onClick={show} data-testid="workshop-notify-preview-button">Voir l'aperçu</Button>}
        <DialogFooter><Button onClick={send} disabled={!preview} data-testid="workshop-notify-send">Envoyer l'e-mail</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const WorkshopPanel = ({ inter, onChanged, readyDays = 7 }) => {
  const ws = inter.workshop || {};
  const [busy, setBusy] = useState(false);
  const [sign, setSign] = useState(false);
  const [notify, setNotify] = useState(null);
  const [returning, setReturning] = useState(false);
  const setStage = async (stage) => {
    setBusy(true);
    try {
      await api.post(`/interventions/${inter.id}/workshop/stage`, { stage });
      toast.success(WORKSHOP_STAGES[stage]);
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  const idx = KEYS.indexOf(ws.stage);
  const next = KEYS[idx + 1];
  const waiting = ws.stage === "pret" ? daysSince(ws.ready_at) : 0;
  const pdf = () => openDepositPdf(inter.id).catch((e) => toast.error(formatApiError(e)));
  return (
    <section className="space-y-4 rounded-xl border border-cyan-400/25 bg-[#122549]/70 p-4" data-testid="workshop-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-display text-base font-semibold text-white md:text-lg">Suivi atelier <span className="font-mono-dt text-sm text-cyan-300" data-testid="workshop-deposit-number">{ws.deposit_number}</span></h2>
        <span className="text-xs text-slate-400">Déposé le {fmtDate(ws.deposit_date)}{ws.expected_date ? ` · prévu le ${fmtDate(ws.expected_date)}` : ""}{ws.returned_at ? ` · restitué le ${fmtDate(ws.returned_at)}` : ""}</span>
      </div>
      <StageBar stage={ws.stage} onPick={(s) => (s === "restitue" && ws.stage !== "restitue" ? setReturning(true) : setStage(s))} busy={busy || inter.draft} />
      {waiting >= readyDays && <p className="rounded-lg bg-amber-500/10 px-3 py-2 text-sm text-amber-200" data-testid="workshop-ready-overdue">Prêt depuis {waiting} jours, non récupéré.</p>}
      <p className="text-sm text-slate-300"><span className="text-slate-500">Accessoires : </span>{(ws.accessories || []).join(", ") || "aucun"}{ws.condition && <><br /><span className="text-slate-500">État à la réception : </span>{ws.condition}</>}</p>
      <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap">
        {next && next !== "restitue" && !inter.draft && <Button className="h-11" disabled={busy} onClick={() => setStage(next)} data-testid="workshop-next-stage">{WORKSHOP_STAGES[next]}<ChevronRight className="ml-1 h-4 w-4" /></Button>}
        {ws.stage === "pret" && <Button className="h-11" disabled={busy} onClick={() => setReturning(true)} data-testid="workshop-return-button"><PackageCheck className="mr-2 h-4 w-4" />Restituer</Button>}
        <Button variant="outline" className="h-11" onClick={pdf} data-testid="workshop-deposit-pdf"><FileText className="mr-2 h-4 w-4" />Bon de dépôt</Button>
        <Button variant="outline" className="h-11" onClick={() => setSign(true)} data-testid="workshop-sign-button"><PenLine className="mr-2 h-4 w-4" />Signature de dépôt</Button>
        {ws.stage === "pret" && <Button variant="outline" className="h-11" onClick={() => setNotify(waiting >= readyDays || ws.ready_email_sent_at ? "reminder" : "ready")} data-testid="workshop-notify-button">
          {waiting >= readyDays || ws.ready_email_sent_at ? <BellRing className="mr-2 h-4 w-4" /> : <Mail className="mr-2 h-4 w-4" />}{waiting >= readyDays || ws.ready_email_sent_at ? "Rappel" : "Prévenir"}</Button>}
      </div>
      {(ws.ready_email_sent_at || ws.reminder_sent_at) && <p className="text-xs text-slate-400" data-testid="workshop-email-status">{ws.ready_email_sent_at && `Client prévenu le ${fmtParis(ws.ready_email_sent_at)}`}{ws.reminder_sent_at && ` · rappel le ${fmtParis(ws.reminder_sent_at)}`}</p>}
      {inter.depot_signed_at && <p className="text-xs text-emerald-300" data-testid="workshop-depot-signed">Dépôt signé par {inter.depot_signature_name}{inter.restitution_signed_at && ` · restitution signée par ${inter.restitution_signature_name}`}</p>}
      <SignatureDialog intervention={sign ? inter : null} onOpenChange={(o) => !o && setSign(false)} onSaved={onChanged} roles={["depot"]} title="Signature du bon de dépôt" onPdf={pdf} />
      <RestitutionDialog inter={returning ? inter : null} onClose={() => setReturning(false)} onSaved={onChanged} />
      {notify && <NotifyDialog inter={inter} reminder={notify === "reminder"} onClose={() => setNotify(null)} onSent={onChanged} />}
    </section>
  );
};
